import React from 'react';
import { Link } from 'react-router-dom';
import { Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { IOfferLogEntry } from 'api/reports';
import {
  formatUtcDateTime,
  minutesBetween,
} from 'services/dates/formatUtcDateTime';
import getOfferStatusLabel from 'services/offers/offerStatusLabel';
import translations from 'services/translations/translations';

/**
 * Who opened, calculated and saved offers: the history of one offer, or with the offer
 * columns the log of all offers.
 */
const OfferLogTable = ({
  entries,
  withOffer,
}: {
  entries: IOfferLogEntry[];
  withOffer?: boolean;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);

  const describe = (entry: IOfferLogEntry) => {
    if (entry.kind === 'status') {
      return t(`${translations.offers}:history.Changed status`, {
        from: getOfferStatusLabel(t, entry.fromStatus ?? '', entry.offerType),
        to: getOfferStatusLabel(t, entry.toStatus ?? '', entry.offerType),
      });
    }

    if (entry.position === null) {
      return t(`${translations.offers}:history.Viewed offer`);
    }

    return t(
      `${translations.offers}:history.${
        entry.calculated ? 'Calculated position' : 'Viewed position'
      }`,
      { position: entry.position }
    );
  };

  return (
    <Table className="offer-log-table" size="sm" responsive>
      <thead>
        <tr>
          <th>{t(`${translations.offers}:history.Opened`)}</th>
          <th>{t(`${translations.offers}:history.User`)}</th>
          {withOffer ? (
            <>
              <th>{t(`${translations.offers}:history.Offer`)}</th>
              <th>{t(`${translations.offers}:history.Department`)}</th>
            </>
          ) : null}
          <th>{t(`${translations.offers}:history.What`)}</th>
          <th>{t(`${translations.offers}:history.Duration`)}</th>
          <th>{t(`${translations.offers}:history.Saved`)}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => (
          <tr
            className={entry.kind === 'status' ? 'offer-log-status' : undefined}
            key={entry.key}
          >
            <td>{formatUtcDateTime(entry.openedAt)}</td>
            <td>{entry.user}</td>
            {withOffer ? (
              <>
                <td>
                  <Link to={`/offers/${entry.offerId}/${entry.offerType}`}>
                    {entry.targetName ?? entry.offerId}
                  </Link>
                </td>
                <td>{entry.department ?? ''}</td>
              </>
            ) : null}
            <td>{describe(entry)}</td>
            <td>
              {entry.kind === 'status'
                ? '-'
                : t(`${translations.offers}:history.Minutes`, {
                    count: minutesBetween(entry.openedAt, entry.until),
                  })}
            </td>
            <td>{entry.savedAt ? formatUtcDateTime(entry.savedAt) : '-'}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default OfferLogTable;
